<script setup lang="ts">
import { ref, watch } from 'vue';
import { getVaultAncestors, type GetVaultAncestorsData, type VaultAncestor } from '@/api';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { contractRoute, vaultDirectoryRoute } from '@/router/links';
import { useVaultStore } from '@/stores/vaultStore';
import { isAccessDenied } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

/**
 * The vault half of a breadcrumb — Contract Vault › folder › … ›
 * contract — for anything that lives in the vault. It goes first in a page's
 * `#breadcrumb` slot; the page follows it with the record's own crumb.
 *
 * The trail is the backend's: root-first, without the object itself, ending
 * with the contract for a contract's file. It stops at the first folder or
 * contract the user cannot read, so a deep link into something shared
 * directly shows just Contract Vault.
 */
const props = defineProps<{
  kind: GetVaultAncestorsData['query']['kind'];
  /** Null while the page is still loading the record. */
  uid: string | null | undefined;
}>();

const vaultStore = useVaultStore();

const trail = ref<VaultAncestor[]>([]);

watch(
  () => [props.kind, props.uid] as const,
  async ([kind, uid]) => {
    trail.value = [];
    if (!uid) return;
    try {
      const [rootUid, response] = await Promise.all([
        vaultStore.resolveRootDirectoryUid(),
        getVaultAncestors({ query: { kind, uid } }),
      ]);
      if (props.uid !== uid || props.kind !== kind) return;
      // The root is the Contract Vault crumb itself. Matched by uid, never by
      // position: a trail cut short by an unreadable folder starts elsewhere.
      trail.value = (response.data ?? []).filter(
        ancestor => !(ancestor.kind === 'directory' && ancestor.uid === rootUid)
      );
    } catch (error) {
      // A refused object keeps the bare Contract Vault crumb; the page itself
      // shows why. Anything else is a real failure.
      if (isAccessDenied(error)) {
        logger.warn('Vault breadcrumb not available:', error);
      } else {
        logger.error('Failed to load the vault breadcrumb:', error);
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <n-breadcrumb-item clickable>
    <router-link :to="{ name: 'file-vault' }">{{ navLabels.fileVault }}</router-link>
  </n-breadcrumb-item>
  <n-breadcrumb-item v-for="ancestor in trail" :key="ancestor.uid" clickable>
    <router-link
      :to="
        ancestor.kind === 'contract'
          ? contractRoute(ancestor.uid)
          : vaultDirectoryRoute(ancestor.uid)
      "
    >
      {{ ancestor.name }}
    </router-link>
  </n-breadcrumb-item>
</template>
